import {Popover, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {RosterPopoverTrigger} from '@/components';
import {TeamAvatarStack, TeamRosterPopoverContent} from '@/features/trust-accounts/members';

import type {TrustAccountListTrustee} from '../utils';
import type {TrustAccountListTeamMemberDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface TrustAccountTeamCellProps {
  trustee: TrustAccountListTrustee;
  team: TrustAccountListTeamMemberDTO[];
  /** Where the dropdown's "Manage team" link goes. */
  onManageTeam?: () => void;
}

/**
 * Everyone on the account — the trustee first, then the team — as an avatar stack beside its
 * size, or "only you" when the caller is the trustee with no team. Opens the team dropdown.
 * Shared by the table's team column and the grid card's footer.
 */
export const TrustAccountTeamCell: FC<TrustAccountTeamCellProps> = ({
  trustee,
  team,
  onManageTeam
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const isOnlyYou = trustee.isCurrentUser && team.length === 0;
  const trusteeLabel = trustee.isCurrentUser
    ? t('trustAccounts:columns.responsibleTrusteeSelf')
    : (trustee.name ?? t('common:na'));

  return (
    <Popover>
      <RosterPopoverTrigger
        overRowLink
        className="w-fit max-w-full"
        ariaLabel={t('trustAccounts:view.header.team.ariaLabel', {
          name: trusteeLabel,
          count: team.length
        })}
      >
        <span data-slot="trust-account-team-cell" className="flex items-center gap-2">
          <TeamAvatarStack members={[trustee, ...team]} size="sm" />
          <Typography as="span" size="xs" color="tertiary" className="tabular-nums">
            {isOnlyYou ? t('trustAccounts:columns.teamOnlyYou') : team.length + 1}
          </Typography>
        </span>
      </RosterPopoverTrigger>
      <TeamRosterPopoverContent
        trustee={trustee}
        teammates={team}
        onManage={() => onManageTeam?.()}
      />
    </Popover>
  );
};
